import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  CallListSortRule,
  CallListType,
  CreateCallListBody,
  CreateCampaignBody,
  DialMode,
  DialableReason,
  DialerDispositionBody,
  ImportCounts,
  ImportField,
  ImportListFields,
  ImportRowFilter,
  ImportRowOutcome,
  ImportRowStatus,
  ListFilter,
  StatisticsPeriod,
  UpdateCallListBody,
  UpdateCampaignBody,
} from '@brimo/shared';
import { api, apiUpload } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import type { CallView, PhonePool } from '../telephony/api';

/** Types and requests of the dialer routes (docs/dev/api-dialer.md). */

export interface EventTypeRef {
  id: string;
  name: string;
  durationMinutes: number;
}

export interface LifecyclePill {
  key: string;
  label: string;
}

export interface MetricCell {
  key: 'talk' | 'reached' | 'appointments' | 'per_talk_hour' | 'streak';
  label: string;
  value: string;
  sub: string | null;
  high: boolean;
}

export interface TodayMetrics {
  cells: MetricCell[];
  record: { sub: string } | null;
  figures: {
    calls: number;
    reached: number;
    talkSeconds: number;
    appointments: number;
    perTalkHour: number;
    streakDays: number;
  };
  bestSameWeekday: { count: number; day: string } | null;
}

export interface ListRow {
  id: string;
  type: CallListType;
  name: string;
  meta: string;
  dialable: number;
  total: number;
  eventType: EventTypeRef | null;
  setterCount: number;
  active: boolean;
}

export interface ListGroups {
  groups: Array<{ type: CallListType; label: string; note: string; rows: ListRow[] }>;
}

export interface UserOption {
  id: string;
  name: string;
  initials: string;
}

export interface Source {
  id: string;
  name: string;
  type: 'webhook' | 'api';
  active: boolean;
  label: string;
  url: string | null;
  today: number;
  sevenDays: number;
  lastLeadAt: string | null;
  lastLeadLabel: string;
}

export interface ListDetail {
  id: string;
  type: CallListType;
  typeLabel: string;
  name: string;
  active: boolean;
  eventType: EventTypeRef | null;
  sortRule: CallListSortRule;
  followUpsFirst: boolean;
  timeWindowStart: string;
  timeWindowEnd: string;
  filters: ListFilter[];
  setters: UserOption[];
  setterOptions: UserOption[];
  campaign: { id: string; name: string; active: boolean; priority: number; sources: Source[] } | null;
  total: number;
  dialable: number;
  maintainedBy: string | null;
  canEdit: boolean;
  linkedCampaign: { id: string; name: string; priority: number } | null;
  import: ImportSummary | null;
}

/** The upload behind an import list (Importlisten, D-238). */
export interface ImportSummary {
  fileName: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  importedAt: string;
  importedAtLabel: string;
  importedBy: string | null;
  totalRows: number;
  processedRows: number;
  counts: ImportCounts;
  errorCount: number;
  failure: string | null;
  stagedCount: number;
  closedAt: string | null;
  closedAtLabel: string | null;
  closedBy: string | null;
  purgedCount: number | null;
  canClose: boolean;
}

export interface ImportErrorRow {
  rowNumber: number;
  name: string;
  phone: string;
  reason: string;
}

export interface ImportPreview {
  fileName: string;
  totalRows: number;
  columns: Array<{ index: number; header: string; samples: string[] }>;
  mapping: ImportField[];
  mappingError: string | null;
  counts: ImportCounts | null;
  sample: Array<{
    rowNumber: number;
    name: string;
    phone: string;
    email: string | null;
    outcome: ImportRowOutcome;
    outcomeLabel: string;
  }>;
  errors: ImportErrorRow[];
  async: boolean;
}

export interface ListContactRow {
  contactId: string;
  name: string;
  phone: string | null;
  /** Entry instant of the contact (ISO, „Eingang"). */
  createdAt: string;
  lifecycle: LifecyclePill;
  lastAttempt: string;
  dialable: true | { reason: DialableReason; detail?: string };
  import: {
    status: ImportRowStatus;
    statusLabel: string;
    staged: boolean;
    rowNumber: number | null;
    canPromote: boolean;
  } | null;
}

export interface ListContactsPage {
  total: number;
  dialable: number;
  rows: ListContactRow[];
  rowsHidden: boolean;
  nextCursor: string | null;
  statusCounts: Record<ImportRowStatus, number> | null;
}

export type ListStart =
  | {
      empty: false;
      listId: string;
      listName: string;
      dialable: number;
      total: number;
      skipped: number;
      eventTypeLine: string | null;
      pool: PhonePool | null;
      orderText: string;
      first: {
        contactId: string;
        name: string;
        phone: string | null;
        listName: string;
        lifecycle: LifecyclePill;
        attempt: number;
        reachability: string | null;
      };
    }
  | {
      empty: true;
      listId: string;
      listName: string;
      total: number;
      outsideWindow: number;
      statusNotDialable: number;
      lockedByOthers: number;
      dialableAgainAt: string | null;
      dialableAgainLabel: string | null;
      thenDialable: number;
      alternative: {
        listId: string;
        name: string;
        dialable: number;
        total: number;
        eventTypeName: string | null;
      } | null;
    };

export interface Overview {
  metrics: TodayMetrics;
  dialable: number;
  followUpsToday: number;
  newLeadsSince8: number;
  listCount: number;
  window: { start: string; end: string; label: string };
  orderText: string;
  pool: PhonePool | null;
  lists: ListRow[];
  activeSessionId: string | null;
}

export interface LeadAnswers {
  interest?: string;
  employment?: string;
  income?: string;
  equity?: string;
  timing?: string;
  reachability?: string;
}

export interface DialerContact {
  id: string;
  name: string;
  phone: string | null;
  phoneDisplay: string | null;
  email: string | null;
  lifecycle: LifecyclePill;
  dnc: boolean;
  attempt: number;
  listName: string | null;
  source: string | null;
  advisor: string | null;
  property: string | null;
  lastContact: string | null;
  leadAnswers: LeadAnswers | null;
  leadLine: string | null;
  briefing: { text: string; tags: string[] } | null;
  importInfo: {
    listId: string;
    listName: string;
    staged: boolean;
    canPromote: boolean;
    extra: Array<{ label: string; value: string }>;
  } | null;
}

export interface DialerTimelineRow {
  id: string;
  occurredAt: string;
  whenLabel: string;
  type: string;
  direction: 'inbound' | 'outbound' | 'internal';
  title: string;
  text: string | null;
  statusLabel: string | null;
}

export interface EventTypeChoice {
  listId: string;
  listName: string;
  eventTypeId: string;
  eventTypeName: string;
  durationMinutes: number;
}

export interface DialerCurrent {
  entryId: string;
  contact: DialerContact;
  timeline: DialerTimelineRow[];
  eventType: EventTypeRef | null;
  eventTypeListName: string | null;
  eventTypeChoice: { lists: EventTypeChoice[] } | null;
  closerLine: string | null;
  call: CallView | null;
  awaitingDisposition: boolean;
}

export interface NextUp {
  contactId: string;
  name: string;
  phone: string | null;
  listName: string | null;
  lifecycle: LifecyclePill;
  attempt: number;
}

export interface DialerSession {
  id: string;
  status: 'running' | 'paused' | 'ended';
  dialMode: DialMode;
  dialModeLabel: string;
  startedAt: string;
  pausedAt: string | null;
  listIds: string[];
}

export interface DialerState {
  session: DialerSession | null;
  progress: { position: number; total: number; percent: number };
  listName: string | null;
  current: DialerCurrent | null;
  nextUp: NextUp | null;
}

export interface Glow {
  appointmentId: string;
  dateLabel: string;
  timeLabel: string;
  line: string;
  calendarLine: string;
  metrics: TodayMetrics;
}

export interface DispositionResponse {
  state: DialerState;
  autoDialInSeconds: number | null;
  record: { sub: string } | null;
  suggestion: 'no_interest' | null;
}

export interface Evaluation {
  sessionId: string;
  status: 'running' | 'paused' | 'ended';
  eyebrow: string;
  figures: Array<{
    key: 'talk' | 'calls' | 'reached' | 'appointments' | 'per_talk_hour' | 'streak';
    label: string;
    value: string;
    sub: string | null;
  }>;
  milestone: { title: string; text: string | null } | null;
  appointments: Array<{ appointmentId: string; name: string; listName: string | null; when: string }>;
  openCount: number;
}

export interface EventTypeOptionGroup {
  key: 'lists' | 'recent' | 'own' | 'organization';
  label: string;
  items: Array<{ eventTypeId: string; name: string; durationMinutes: number; meta: string }>;
}

export interface EventTypeOptions {
  groups: EventTypeOptionGroup[];
  canCreate: boolean;
}

export interface Statistics {
  userId: string;
  userName: string;
  /** External company of the shown setter's membership (0037). */
  userExternalCompany: string | null;
  periodLabel: string;
  kpis: {
    dials: number;
    conversations: number;
    talkSeconds: number;
    talkLabel: string;
    appointments: number;
    attended: number;
    attendedPct: number;
  };
  results: Array<{
    key: 'appointment_set' | 'follow_up' | 'not_reached' | 'no_interest' | 'wrong_number' | 'block';
    label: string;
    count: number;
    basis: 'conversations' | 'dials';
    pct: number;
  }>;
  perList: Array<{ listId: string | null; name: string; appointments: number }>;
  canSeeTeam: boolean;
  users: Array<{ id: string; name: string; externalCompany: string | null }>;
}

export interface Campaign {
  id: string;
  name: string;
  active: boolean;
  priority: number;
  listId: string | null;
  eventType: EventTypeRef | null;
  sources: Source[];
}

export interface SlotsResponse {
  eventTypeId: string;
  durationMinutes: number;
  days: Array<{ date: string; slots: Array<{ startsAt: string; endsAt: string; advisorId: string }> }>;
}

export interface ContactSearchItem {
  id: string;
  firstName: string;
  lastName: string;
  lifecycle: LifecyclePill;
  dnc: boolean;
}

// --- keys --------------------------------------------------------------------------------------------

export const dialerKeys = {
  overview: () => [...queryRoots.dialer, 'overview'] as const,
  metrics: () => [...queryRoots.dialer, 'metrics'] as const,
  lists: () => [...queryRoots.dialer, 'lists'] as const,
  list: (id: string) => [...queryRoots.dialer, 'list', id] as const,
  listContacts: (id: string, status: ImportRowFilter = 'all') =>
    [...queryRoots.dialer, 'list', id, 'contacts', status] as const,
  listStart: (id: string) => [...queryRoots.dialer, 'list', id, 'start'] as const,
  previewCount: (filters: ListFilter[]) => [...queryRoots.dialer, 'preview-count', filters] as const,
  current: () => [...queryRoots.dialer, 'session', 'current'] as const,
  evaluation: (id: string) => [...queryRoots.dialer, 'session', id, 'evaluation'] as const,
  eventTypeOptions: (contactId: string | null, q: string) =>
    [...queryRoots.dialer, 'event-type-options', contactId, q] as const,
  statistics: (period: StatisticsPeriod, userId: string | null) =>
    [...queryRoots.dialer, 'statistics', period, userId] as const,
  slots: (eventTypeId: string, from: string, to: string) =>
    [...queryRoots.slots, 'dialer', eventTypeId, from, to] as const,
  contactSearch: (q: string) => [...queryRoots.contacts, 'dialer-search', q] as const,
  campaigns: () => [...queryRoots.dialer, 'campaigns'] as const,
};

function query(params: Record<string, string | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const text = search.toString();
  return text ? `?${text}` : '';
}

// --- queries -----------------------------------------------------------------------------------------

export function useOverview() {
  return useQuery({ queryKey: dialerKeys.overview(), queryFn: () => api<Overview>('/dialer/overview') });
}

export function useTodayMetrics(enabled = true) {
  return useQuery({
    queryKey: dialerKeys.metrics(),
    queryFn: () => api<TodayMetrics>('/dialer/metrics/today'),
    enabled,
  });
}

export function useListGroups() {
  return useQuery({ queryKey: dialerKeys.lists(), queryFn: () => api<ListGroups>('/dialer/lists') });
}

export function useListDetail(id: string) {
  return useQuery({ queryKey: dialerKeys.list(id), queryFn: () => api<ListDetail>(`/dialer/lists/${id}`) });
}

export function useListContacts(id: string, status: ImportRowFilter = 'all') {
  return useInfiniteQuery({
    queryKey: dialerKeys.listContacts(id, status),
    queryFn: ({ pageParam, signal }) =>
      api<ListContactsPage>(
        `/dialer/lists/${id}/contacts${query({ cursor: pageParam, status: status === 'all' ? null : status })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** Campaigns for the optional link of an import list (`GET /dialer/campaigns`). */
export function useCampaigns(enabled: boolean) {
  return useQuery({
    queryKey: dialerKeys.campaigns(),
    queryFn: () => api<{ items: Campaign[] }>('/dialer/campaigns'),
    enabled,
  });
}

export function useListStart(id: string) {
  return useQuery({
    queryKey: dialerKeys.listStart(id),
    queryFn: () => api<ListStart>(`/dialer/lists/${id}/start`),
    retry: false,
  });
}

export function usePreviewCount(filters: ListFilter[], enabled: boolean) {
  return useQuery({
    queryKey: dialerKeys.previewCount(filters),
    queryFn: ({ signal }) =>
      api<{ total: number; dialable: number }>('/dialer/lists/preview-count', {
        method: 'POST',
        body: { filters },
        signal,
      }),
    enabled,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useDialerState() {
  return useQuery({
    queryKey: dialerKeys.current(),
    queryFn: () => api<DialerState>('/dialer/sessions/current'),
  });
}

export function useEvaluation(id: string | null) {
  return useQuery({
    queryKey: dialerKeys.evaluation(id ?? ''),
    queryFn: () => api<Evaluation>(`/dialer/sessions/${id ?? ''}/evaluation`),
    enabled: id !== null,
  });
}

export function useEventTypeOptions(contactId: string | null, q: string, enabled: boolean) {
  return useQuery({
    queryKey: dialerKeys.eventTypeOptions(contactId, q),
    queryFn: ({ signal }) =>
      api<EventTypeOptions>(`/dialer/event-type-options${query({ contactId, q: q.trim() || null })}`, {
        signal,
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useStatistics(period: StatisticsPeriod, userId: string | null) {
  return useQuery({
    queryKey: dialerKeys.statistics(period, userId),
    queryFn: () => api<Statistics>(`/dialer/statistics${query({ period, userId })}`),
    placeholderData: keepPreviousData,
  });
}

export function useSlots(eventTypeId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: dialerKeys.slots(eventTypeId ?? '', from, to),
    queryFn: ({ signal }) =>
      api<SlotsResponse>(`/slots${query({ eventTypeId, from, to, internal: '1' })}`, { signal }),
    enabled: eventTypeId !== null,
  });
}

export function useContactSearch(q: string, enabled: boolean) {
  return useQuery({
    queryKey: dialerKeys.contactSearch(q),
    queryFn: ({ signal }) =>
      api<{ items: ContactSearchItem[] }>(`/contacts${query({ q: q.trim(), limit: '20', sort: 'name' })}`, {
        signal,
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

// --- mutations ---------------------------------------------------------------------------------------

function importForm(file: File, fields: Record<string, unknown>): FormData {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) form.append(key, JSON.stringify(value));
  }
  form.append('file', file);
  return form;
}

/** `POST /dialer/lists/import/preview`: columns, suggested/given mapping, counts and sample. */
export function previewImport(file: File, mapping?: ImportField[]): Promise<ImportPreview> {
  return apiUpload<ImportPreview>('/dialer/lists/import/preview', importForm(file, { mapping }));
}

/** `POST /dialer/lists/import`: creates the import list (synchronously up to 2 000 rows, else in the background). */
export function importList(file: File, mapping: ImportField[], list: ImportListFields): Promise<ListDetail> {
  return apiUpload<ListDetail>('/dialer/lists/import', importForm(file, { mapping, list }));
}

export function fetchImportErrors(id: string): Promise<{ rows: ImportErrorRow[] }> {
  return api<{ rows: ImportErrorRow[] }>(`/dialer/lists/${id}/import/errors`);
}

export function promoteListContact(
  id: string,
  contactId: string,
): Promise<{ contactId: string; promoted: true }> {
  return api(`/dialer/lists/${id}/contacts/${contactId}/promote`, { method: 'POST' });
}

export function closeImportList(id: string): Promise<ListDetail> {
  return api<ListDetail>(`/dialer/lists/${id}/close`, { method: 'POST' });
}

export function createList(body: CreateCallListBody): Promise<ListDetail> {
  return api<ListDetail>('/dialer/lists', { method: 'POST', body });
}

export function updateList(id: string, body: UpdateCallListBody): Promise<ListDetail> {
  return api<ListDetail>(`/dialer/lists/${id}`, { method: 'PATCH', body });
}

export function putListFilters(id: string, filters: ListFilter[]): Promise<ListDetail> {
  return api<ListDetail>(`/dialer/lists/${id}/filters`, { method: 'PUT', body: { filters } });
}

export function putListSetters(id: string, userIds: string[]): Promise<ListDetail> {
  return api<ListDetail>(`/dialer/lists/${id}/setters`, { method: 'PUT', body: { userIds } });
}

export function addListContacts(
  id: string,
  contactIds: string[],
): Promise<{ added: number; alreadyInList: number; blocked: number }> {
  return api(`/dialer/lists/${id}/contacts`, { method: 'POST', body: { contactIds } });
}

export function removeListContact(id: string, contactId: string): Promise<void> {
  return api<void>(`/dialer/lists/${id}/contacts/${contactId}`, { method: 'DELETE' });
}

export function createCampaign(body: CreateCampaignBody): Promise<Campaign> {
  return api<Campaign>('/dialer/campaigns', { method: 'POST', body });
}

export function updateCampaign(id: string, body: UpdateCampaignBody): Promise<Campaign> {
  return api<Campaign>(`/dialer/campaigns/${id}`, { method: 'PATCH', body });
}

export function createSource(campaignId: string, body: { name: string; type: 'webhook' | 'api' }) {
  return api<Source>(`/dialer/campaigns/${campaignId}/sources`, { method: 'POST', body });
}

export function startSession(body: { listIds?: string[]; dialMode: DialMode }): Promise<DialerState> {
  return api<DialerState>('/dialer/sessions', { method: 'POST', body });
}

export function sessionAction(id: string, action: 'next' | 'pause' | 'resume'): Promise<DialerState> {
  return api<DialerState>(`/dialer/sessions/${id}/${action}`, { method: 'POST', body: {} });
}

export function endSession(id: string): Promise<Evaluation> {
  return api<Evaluation>(`/dialer/sessions/${id}/end`, { method: 'POST', body: {} });
}

export function bookFromSession(
  id: string,
  body: { eventTypeId: string; startsAt: string; advisorId?: string },
): Promise<Glow> {
  return api<Glow>(`/dialer/sessions/${id}/book`, { method: 'POST', body });
}

export function saveDisposition(id: string, body: DialerDispositionBody): Promise<DispositionResponse> {
  return api<DispositionResponse>(`/dialer/sessions/${id}/disposition`, { method: 'POST', body });
}
